/**
 * Event-driven state for the calendar. applyEvent(state, event) updates state from one event.
 */
import type { AvatarBitmap } from "./avatars";
import { decodeAvatarFromEventData } from "./avatars";

export interface SlotTag {
  tag_id: number;
  tag_name: string;
}

export interface Slot {
  id: number;
  user_id: number;
  date: string;
  start_time: string;
  end_time: string;
  description: string;
  creator_name: string;
  tags: SlotTag[];
  reaction_counts: Record<string, number>;
  current_user_emoji?: string;
}

export interface LogEntry {
  id: number;
  type: string;
  message: string;
  username: string;
  created_at: string;
}

export interface State {
  slotsById: Record<number, Slot>;
  slotsByDate: Record<string, Slot[]>;
  tagsById: Record<number, { id: number; name: string }>;
  tagsBySlot: Record<number, SlotTag[]>;
  reactionsBySlot: Record<number, Record<string, number>>;
  /** Per-user emoji per slot: slot_id -> username -> emoji. Ensures one vote per user. */
  userReactionBySlot: Record<number, string>;
  reactionsByUserBySlot: Record<number, Record<string, string>>;
  /** Chat message reactions: message_id (event id) -> emoji -> count */
  reactionsByMessage: Record<number, Record<string, number>>;
  /** Per-user emojis per chat message: message_id -> username -> emoji[] (multiple per user) */
  reactionsByUserByMessage: Record<number, Record<string, string[]>>;
  logMessages: LogEntry[];
  /** Latest face avatar bitmap per username (per calendar). */
  avatarsByUsername: Record<string, AvatarBitmap>;
  /** Human-readable calendar name derived from calendar_renamed events (optional). */
  calendarName?: string | null;
  /** From username_changed events: maps previous name -> new name so we can resolve display name from cookie. */
  displayNameChain?: Record<string, string>;
  lastEventId: number;
}

export interface EventData {
  slot_id?: number;
  message_id?: number;
  user_id?: number;
  date?: string;
  start_time?: string;
  end_time?: string;
  description?: string;
  creator_name?: string;
  tag_id?: number;
  tag_name?: string;
  emoji?: string;
  [key: string]: unknown;
}

export interface AppEvent {
  id: number;
  type: string;
  data?: EventData | null;
  message?: string;
  username?: string;
  created_at?: string;
}

export const EMOJI_DISPLAY: Record<string, string> = {
  heart: '❤',
  laugh: '😂',
  thumbs_up: '👍',
  thumbs_down: '👎',
};

const USER_COLOR_CLASSES = [
  'text-brutal-purple border-brutal-purple',
  'text-brutal-orange border-brutal-orange',
  'text-emerald-600 border-emerald-600',
  'text-rose-600 border-rose-600',
  'text-sky-600 border-sky-600',
  'text-amber-700 border-amber-700',
];

export function usernameColorClass(username: string | null | undefined): string {
  const name = username?.trim() || 'System';
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash * 31 + name.charCodeAt(i)) | 0;
  }
  const idx = Math.abs(hash) % USER_COLOR_CLASSES.length;
  return USER_COLOR_CLASSES[idx];
}

/** Returns emojis currently used in the calendar (slots + chat) as unicode chars. Skips shortcodes. */
export function getUsedEmojis(state: State): string[] {
  const used = new Set<string>();
  for (const slot of Object.values(state.slotsById || {})) {
    for (const shortcode of Object.keys(slot.reaction_counts || {})) {
      const emoji = EMOJI_DISPLAY[shortcode];
      if (emoji) used.add(emoji);
    }
  }
  for (const counts of Object.values(state.reactionsByMessage || {})) {
    for (const emoji of Object.keys(counts || {})) {
      if (!/^[a-z_]+$/.test(emoji)) used.add(emoji);
    }
  }
  return [...used];
}

/** Returns Tailwind classes for a tag label (shared by calendar and slot row). */
export function tagLabelClass(tagName: string): string {
  const n = tagName.toLowerCase();
  if (n === 'winner') return 'bg-brutal-purple text-white dark:bg-brutal-purple-hover dark:text-white';
  if (n === 'vetoed') return 'bg-orange-700 text-white dark:bg-orange-600 dark:text-white';
  return 'bg-brutal-orange text-black dark:bg-brutal-orange-dark dark:text-gray-900';
}

export function initialState(): State {
  return {
    slotsById: {},
    slotsByDate: {},
    tagsById: {},
    tagsBySlot: {},
    reactionsBySlot: {},
    userReactionBySlot: {},
    reactionsByUserBySlot: {},
    reactionsByMessage: {},
    reactionsByUserByMessage: {},
    logMessages: [],
    avatarsByUsername: {},
    calendarName: null,
    displayNameChain: undefined,
    lastEventId: 0,
  };
}

/** Resolve current display name from cookie username by following username_changed chain in state. */
export function resolveDisplayName(
  cookieUsername: string,
  chain?: Record<string, string> | null
): string {
  if (!chain || !cookieUsername) return cookieUsername;
  let current = cookieUsername;
  const seen = new Set<string>();
  while (chain[current] && !seen.has(current)) {
    seen.add(current);
    current = chain[current];
  }
  return current;
}

export interface ApplyEventOptions {
  username?: string | null;
}

export function applyEvent(
  state: State,
  event: AppEvent,
  options: ApplyEventOptions = {}
): State {
  const currentUsername = options.username ?? null;
  const { id, type, data, message, username, created_at } = event;
  const next: State = { ...state };
  next.lastEventId = id;
  const isChatReaction =
    type === 'chat_message_reaction_added' ||
    type === 'chat_message_reaction_removed';
  if (!isChatReaction) {
    const logEntry: LogEntry = {
      id,
      type,
      message: message ?? '',
      username: username ?? '',
      created_at: created_at ?? '',
    };
    next.logMessages = [...(state.logMessages || []), logEntry];
  }

  if (!data || typeof data !== 'object') return next;

  switch (type) {
    case 'calendar_renamed': {
      const raw = (data as { name?: unknown }).name;
      const name = typeof raw === 'string' ? raw.trim() : '';
      next.calendarName = name || null;
      break;
    }
    case 'username_changed': {
      const raw = (data as { name?: unknown }).name;
      const toName = typeof raw === 'string' ? raw.trim() : '';
      if (toName && username) {
        next.displayNameChain = { ...(state.displayNameChain ?? {}), [username]: toName };
        // New name inherits the previous name's avatar so the log shows the same face.
        const prevAvatar = state.avatarsByUsername?.[username];
        if (prevAvatar) {
          next.avatarsByUsername = { ...(next.avatarsByUsername ?? {}), [toName]: prevAvatar };
        }
      }
      break;
    }
    case 'slot_created': {
      const slot: Slot = {
        id: data.slot_id!,
        user_id: data.user_id!,
        date: data.date!,
        start_time: data.start_time!,
        end_time: data.end_time!,
        description: data.description ?? '',
        creator_name: data.creator_name ?? '',
        tags: next.tagsBySlot[data.slot_id!] ?? [],
        reaction_counts: next.reactionsBySlot[data.slot_id!] ?? {},
        current_user_emoji: next.userReactionBySlot[data.slot_id!] ?? '',
      };
      next.slotsById = { ...next.slotsById, [data.slot_id!]: slot };
      const date = data.date!;
      next.slotsByDate = { ...next.slotsByDate };
      next.slotsByDate[date] = [...(next.slotsByDate[date] || []), slot];
      break;
    }
    case 'slot_deleted': {
      const sid = data.slot_id!;
      const slot = next.slotsById[sid];
      if (slot) {
        const { [sid]: _, ...rest } = next.slotsById;
        next.slotsById = rest;
        const slotDate = slot.date;
        next.slotsByDate = { ...next.slotsByDate };
        next.slotsByDate[slotDate] = (next.slotsByDate[slotDate] || []).filter(
          (s) => s.id !== sid
        );
      }
      break;
    }
    case 'slot_description_updated': {
      const slot = next.slotsById[data.slot_id!];
      if (slot) {
        next.slotsById = {
          ...next.slotsById,
          [data.slot_id!]: { ...slot, description: data.description ?? '' },
        };
        const slotDate = slot.date;
        next.slotsByDate = { ...next.slotsByDate };
        next.slotsByDate[slotDate] = (next.slotsByDate[slotDate] || []).map(
          (s) =>
            s.id === data.slot_id!
              ? { ...s, description: data.description ?? '' }
              : s
        );
      }
      break;
    }
    case 'reaction_set': {
      const sid = data.slot_id!;
      const emoji = data.emoji!;
      const who = String(data.username ?? '');
      next.reactionsBySlot = { ...next.reactionsBySlot };
      next.reactionsByUserBySlot = { ...next.reactionsByUserBySlot };
      const userMap: Record<string, string> = next.reactionsByUserBySlot[sid] ?? {};
      const prevEmoji = userMap[who];
      const counts: Record<string, number> = { ...(next.reactionsBySlot[sid] || {}) };
      if (prevEmoji) {
        counts[prevEmoji] = Math.max(0, (counts[prevEmoji] || 0) - 1);
        if (counts[prevEmoji] === 0) delete counts[prevEmoji];
      }
      counts[emoji] = (counts[emoji] || 0) + 1;
      next.reactionsBySlot[sid] = counts;
      next.reactionsByUserBySlot[sid] = { ...userMap, [who]: emoji };
      if (currentUsername != null && who === currentUsername) {
        next.userReactionBySlot = { ...next.userReactionBySlot, [sid]: emoji };
      }
      const slot = next.slotsById[sid];
      if (slot) {
        next.slotsById = { ...next.slotsById, [sid]: { ...slot, reaction_counts: counts } };
        const slotDate = slot.date;
        next.slotsByDate = { ...next.slotsByDate };
        next.slotsByDate[slotDate] = (next.slotsByDate[slotDate] || []).map(
          (s) => (s.id === sid ? { ...s, reaction_counts: counts } : s)
        );
      }
      break;
    }
    case 'reaction_cleared': {
      const sid = data.slot_id!;
      const who = String(data.username ?? '');
      next.reactionsByUserBySlot = { ...next.reactionsByUserBySlot };
      const userMap: Record<string, string> = next.reactionsByUserBySlot[sid] ?? {};
      const clearedEmoji = (data.emoji as string | undefined) ?? userMap[who] ?? next.userReactionBySlot[sid];
      const m: Record<string, string> = { ...userMap };
      delete m[who];
      next.reactionsByUserBySlot[sid] = Object.keys(m).length ? m : {};
      if (currentUsername != null && who === currentUsername) {
        next.userReactionBySlot = { ...next.userReactionBySlot };
        delete next.userReactionBySlot[sid];
      }
      next.reactionsBySlot = { ...next.reactionsBySlot };
      const counts: Record<string, number> = { ...(next.reactionsBySlot[sid] || {}) };
      if (clearedEmoji) {
        counts[clearedEmoji] = Math.max(0, (counts[clearedEmoji] || 0) - 1);
        if (counts[clearedEmoji] === 0) delete counts[clearedEmoji];
      }
      next.reactionsBySlot[sid] = counts;
      const slot = next.slotsById[sid];
      if (slot) {
        next.slotsById = { ...next.slotsById, [sid]: { ...slot, reaction_counts: counts } };
        const slotDate = slot.date;
        next.slotsByDate = { ...next.slotsByDate };
        next.slotsByDate[slotDate] = (next.slotsByDate[slotDate] || []).map(
          (s) => (s.id === sid ? { ...s, reaction_counts: counts } : s)
        );
      }
      break;
    }
    case 'tag_created': {
      next.tagsById = {
        ...next.tagsById,
        [data.tag_id!]: { id: data.tag_id!, name: String(data.name ?? '') },
      };
      break;
    }
    case 'tag_added_to_slot': {
      const sid = data.slot_id!;
      next.tagsBySlot = { ...next.tagsBySlot };
      next.tagsBySlot[sid] = [
        ...(next.tagsBySlot[sid] || []),
        { tag_id: data.tag_id!, tag_name: data.tag_name! },
      ];
      const slot = next.slotsById[sid];
      if (slot) {
        const tags = next.tagsBySlot[sid] || [];
        next.slotsById = { ...next.slotsById, [sid]: { ...slot, tags } };
        const slotDate = slot.date;
        next.slotsByDate = { ...next.slotsByDate };
        next.slotsByDate[slotDate] = (next.slotsByDate[slotDate] || []).map(
          (s) => (s.id === sid ? { ...s, tags } : s)
        );
      }
      break;
    }
    case 'tag_removed_from_slot': {
      const sid = data.slot_id!;
      next.tagsBySlot = { ...next.tagsBySlot };
      next.tagsBySlot[sid] = (next.tagsBySlot[sid] || []).filter(
        (t) => t.tag_id !== data.tag_id
      );
      const slot = next.slotsById[sid];
      if (slot) {
        const tags = next.tagsBySlot[sid] || [];
        next.slotsById = { ...next.slotsById, [sid]: { ...slot, tags } };
        const slotDate = slot.date;
        next.slotsByDate = { ...next.slotsByDate };
        next.slotsByDate[slotDate] = (next.slotsByDate[slotDate] || []).map(
          (s) => (s.id === sid ? { ...s, tags } : s)
        );
      }
      break;
    }
    case 'chat_message_posted':
      break;
    case 'chat_message_reaction_added': {
      const mid = data.message_id!;
      const emoji = data.emoji!;
      const who = String(data.username ?? '');
      next.reactionsByMessage = { ...next.reactionsByMessage };
      next.reactionsByUserByMessage = { ...next.reactionsByUserByMessage };
      const userMap = next.reactionsByUserByMessage[mid] ?? {};
      const userEmojis: string[] = Array.isArray(userMap[who])
        ? userMap[who]
        : userMap[who]
          ? [userMap[who] as unknown as string]
          : [];
      if (!userEmojis.includes(emoji)) {
        const nextEmojis = [...userEmojis, emoji];
        const counts: Record<string, number> = {
          ...(next.reactionsByMessage[mid] || {}),
        };
        counts[emoji] = (counts[emoji] || 0) + 1;
        next.reactionsByMessage[mid] = counts;
        next.reactionsByUserByMessage[mid] = {
          ...userMap,
          [who]: nextEmojis,
        };
      }
      break;
    }
    case 'chat_message_reaction_removed': {
      const mid = data.message_id!;
      let emoji = (data.emoji as string) ?? '';
      const who = String(data.username ?? '');
      next.reactionsByUserByMessage = { ...next.reactionsByUserByMessage };
      const userMap = next.reactionsByUserByMessage[mid] ?? {};
      const userEmojis: string[] = Array.isArray(userMap[who])
        ? userMap[who]
        : userMap[who]
          ? [userMap[who] as unknown as string]
          : [];
      if (!emoji && userEmojis.length > 0) {
        emoji = userEmojis[0];
      }
      const nextEmojis = userEmojis.filter((e) => e !== emoji);
      next.reactionsByUserByMessage[mid] =
        nextEmojis.length > 0
          ? { ...userMap, [who]: nextEmojis }
          : (() => {
              const m = { ...userMap };
              delete m[who];
              return Object.keys(m).length ? m : ({} as Record<string, string[]>);
            })();
      next.reactionsByMessage = { ...next.reactionsByMessage };
      const counts: Record<string, number> = {
        ...(next.reactionsByMessage[mid] || {}),
      };
      if (emoji) {
        counts[emoji] = Math.max(0, (counts[emoji] || 0) - 1);
        if (counts[emoji] === 0) delete counts[emoji];
      }
      next.reactionsByMessage[mid] = counts;
      break;
    }
    case 'user_face_set': {
      const anyData = data as { username?: unknown };
      const who = String(anyData.username ?? username ?? '').trim();
      if (!who) {
        break;
      }
      const decoded = decodeAvatarFromEventData(data);
      const avatars = { ...(state.avatarsByUsername || {}) };
      if (decoded) {
        avatars[who] = decoded;
      } else {
        delete avatars[who];
      }
      next.avatarsByUsername = avatars;
      break;
    }
    default:
      break;
  }
  return next;
}
